import type { Entries, EventRuleModel, ExtractorModel, KeyMappingModel, YamlKey, YamlScalar } from './extractor-model'
import { fromYaml, writtenStreams } from './extractor-model'
import { toJsRegex } from './extractor-regex'
import { DEFAULT_KV_DECODER_REGEX, DEFAULT_KV_REGEX } from './extractor-grammar-facts'

/**
 * Event rules over samples — W5.3 (docs/extractors-requirements.md §W5, stages 4–5).
 *
 * Everything the Events and Map stages show is computed here, client-side,
 * from the model and the samples: which rule claims which sample (first
 * match wins, like the engine), what a rule's decoder yields per line (named
 * groups / key-value pairs / flattened JSON), which event keys the samples
 * produce, and an offline simulation of the whole extractor for demo mode.
 * The real engine is `parsers/play`; the simulator is labelled as such.
 *
 * Decoders, as the corpus uses them (docs/inventory/extractor-grammar.md):
 *   regex   decoder-regex with Python named groups
 *   json    the line is a JSON object; keys (and nested `a[b]` paths) are the groups
 *   custom  key-value: `decoder-regex` is a two-group key/value pattern applied
 *           repeatedly; `decoder-header: CEF | LEEF` strips the header first and
 *           exposes its fields as `Header-1` … `Header-n`
 */

const s = (v: YamlScalar | undefined): string => (v === undefined || v === null ? '' : String(v))

/* --------------------------------------------------------------- groups */

/** Flatten a JSON object into `{ a: '…', 'a[b]': '…' }` — the corpus's `{event[action]}` key formats. */
export function flattenJson(value: unknown, prefix = '', out: Record<string, string> = {}): Record<string, string> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const key = prefix ? `${prefix}[${k}]` : k
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        out[key] = JSON.stringify(v)
        flattenJson(v, key, out)
      } else out[key] = v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v)
    }
  }
  return out
}

/** CEF/LEEF: the pipe-separated header fields and the extension tail. */
export function splitHeader(line: string, header: string): { fields: string[]; rest: string } {
  const n = header.toUpperCase() === 'LEEF' ? 5 : 7
  const at = line.search(/(?:CEF|LEEF):/i)
  const body = at >= 0 ? line.slice(at) : line
  const parts: string[] = []
  let cur = ''
  for (let i = 0; i < body.length && parts.length < n; i++) {
    const ch = body[i]!
    if (ch === '\\' && i + 1 < body.length) { cur += body[i + 1]; i++; continue }
    if (ch === '|') { parts.push(cur); cur = '' ; continue }
    cur += ch
  }
  // Position after the n-th unescaped pipe.
  let pipes = 0, idx = 0
  for (; idx < body.length && pipes < n; idx++) {
    if (body[idx] === '\\') { idx++; continue }
    if (body[idx] === '|') pipes++
  }
  return { fields: parts, rest: pipes === n ? body.slice(idx) : '' }
}

/** Key/value pairs from a two-group decoder-regex applied repeatedly (the key-value decoder). */
export function keyValuePairs(text: string, decoderRegex: string): Array<[string, string]> {
  const re = toJsRegex(decoderRegex, 'g')
  if (!re) return []
  const out: Array<[string, string]> = []
  let m: RegExpExecArray | null
  let guard = 0
  while ((m = re.exec(text)) && guard++ < 500) {
    if (m[0] === '') { re.lastIndex++; continue }
    const groups = m.slice(1).filter((x) => x !== undefined)
    if (groups.length >= 2) out.push([String(groups[0]).trim(), String(groups[1]).trim()])
  }
  return out
}

/** What a rule's decoder yields for one line: the groups the mappings translate from. */
export function extractGroups(model: ExtractorModel, rule: EventRuleModel, line: string): Record<string, string> {
  const decoder = s(rule.decoder).toLowerCase()
  if (decoder === 'json') {
    try { return flattenJson(JSON.parse(line)) } catch { return {} }
  }
  if (decoder === 'custom') {
    const groups: Record<string, string> = {}
    let text = line
    if (rule.decoderHeader !== undefined && rule.decoderHeader !== null) {
      const { fields, rest } = splitHeader(line, s(rule.decoderHeader))
      fields.forEach((f, i) => { groups[`Header-${i + 1}`] = f })
      text = rest || line
    }
    const kv = s(rule.decoderRegex) || s(model.kvRegex).replace('%KEY%', '(\\S+?)') || DEFAULT_KV_DECODER_REGEX
    for (const [k, v] of keyValuePairs(text, kv)) if (!(k in groups)) groups[k] = v
    return groups
  }
  const re = rule.decoderRegex === undefined ? null : toJsRegex(s(rule.decoderRegex))
  return (re?.exec(line)?.groups ?? {}) as Record<string, string>
}

/** `{name}` and `{a[b]}` placeholders filled from the groups; a literal format stays itself. */
export function formatKey(keyFormat: YamlScalar | undefined, groups: Record<string, string>): string {
  const f = s(keyFormat)
  return f.replace(/\{([^}]+)\}/g, (_, g: string) => groups[g] ?? '')
}

/** Group spans for highlighting a sample regex101-style; empty when the pattern does not compile or match. */
export function groupSpans(decoderRegex: string, line: string): Array<{ name: string; value: string; start: number; end: number }> {
  const re = toJsRegex(decoderRegex, 'd')
  const m = re?.exec(line)
  const indices = (m as (RegExpExecArray & { indices?: { groups?: Record<string, [number, number] | undefined> } }) | null)?.indices?.groups
  if (!m || !indices) return []
  return Object.entries(indices)
    .filter((e): e is [string, [number, number]] => Array.isArray(e[1]))
    .map(([name, [start, end]]) => ({ name, value: line.slice(start, end), start, end }))
    .sort((a, b) => a.start - b.start)
}

/* -------------------------------------------------------------- buckets */

export interface Buckets {
  /** Sample indices per rule, first match wins. */
  byRule: number[][]
  /** Samples no rule's first-match hits — the future fallout. */
  unmatched: number[]
  /** Rules whose first-match does not compile here. */
  invalid: number[]
}

export function bucketSamples(model: ExtractorModel, samples: string[]): Buckets {
  const compiled = model.rules.map((r) => (s(r.firstMatch) ? toJsRegex(s(r.firstMatch)) : null))
  const byRule: number[][] = model.rules.map(() => [])
  const unmatched: number[] = []
  const invalid = compiled.map((re, i) => (re === null && s(model.rules[i]!.firstMatch) ? i : -1)).filter((i) => i >= 0)
  samples.forEach((line, si) => {
    const ri = compiled.findIndex((re) => re?.test(line))
    if (ri >= 0) byRule[ri]!.push(si)
    else unmatched.push(si)
  })
  return { byRule, unmatched, invalid }
}

/* ------------------------------------------------------------ inference */

export interface DecoderGuess {
  decoder: 'regex' | 'json' | 'custom'
  header?: 'CEF' | 'LEEF'
  /** For `custom`: the two-group key/value regex to set as decoder-regex, and the top-level kv-regex. */
  decoderRegex?: string
  kvRegex?: string
  why: string
}

/**
 * Corpus rule (W5.0): every line parses as a JSON object → json; a CEF: or
 * LEEF: header → custom with that header; three or more key=value pairs on
 * most lines → custom key-value; otherwise regex.
 */
export function inferDecoder(lines: string[]): DecoderGuess {
  const ls = lines.filter((l) => l.trim())
  if (!ls.length) return { decoder: 'regex', why: 'no samples yet' }
  const json = ls.every((l) => { try { const j = JSON.parse(l); return j && typeof j === 'object' && !Array.isArray(j) } catch { return false } })
  if (json) return { decoder: 'json', why: 'every sample is a JSON object' }
  const cef = ls.filter((l) => /\bCEF:\d/.test(l)).length
  if (cef >= Math.ceil(ls.length / 2)) return { decoder: 'custom', header: 'CEF', decoderRegex: DEFAULT_KV_DECODER_REGEX, kvRegex: DEFAULT_KV_REGEX, why: `${cef} of ${ls.length} samples carry a CEF header` }
  const leef = ls.filter((l) => /\bLEEF:\d/.test(l)).length
  if (leef >= Math.ceil(ls.length / 2)) return { decoder: 'custom', header: 'LEEF', decoderRegex: DEFAULT_KV_DECODER_REGEX, kvRegex: DEFAULT_KV_REGEX, why: `${leef} of ${ls.length} samples carry a LEEF header` }
  const kv = ls.filter((l) => (l.match(/\b[\w.-]+=(?:"[^"]*"|\S+)/g) ?? []).length >= 3).length
  if (kv >= Math.ceil(ls.length / 2)) return { decoder: 'custom', decoderRegex: DEFAULT_KV_DECODER_REGEX, kvRegex: DEFAULT_KV_REGEX, why: `${kv} of ${ls.length} samples carry three or more key=value pairs` }
  return { decoder: 'regex', why: 'free text — a decoder-regex with named groups' }
}

/* ------------------------------------------------------------ per rule */

/** Distinct event keys the rule's matched samples format to, in first-seen order. */
export function keysSeen(model: ExtractorModel, rule: EventRuleModel, lines: string[]): string[] {
  const seen = new Set<string>()
  for (const l of lines) seen.add(formatKey(rule.keyFormat, extractGroups(model, rule, l)))
  return [...seen]
}

/** The source names a translate row can pick from: named groups, or the keys the decoder found. */
export function groupNames(model: ExtractorModel, rule: EventRuleModel, lines: string[]): string[] {
  const names = new Set<string>()
  if (s(rule.decoder).toLowerCase() === 'regex' || (!s(rule.decoder) && rule.decoderRegex !== undefined)) {
    for (const m of s(rule.decoderRegex).matchAll(/\(\?P<([A-Za-z_]\w*)>/g)) names.add(m[1]!)
  }
  for (const l of lines) for (const k of Object.keys(extractGroups(model, rule, l))) names.add(k)
  return [...names]
}

/** A rule for the Events stage: a first-match, the regex decoder, one mapping on the stream. */
export function newRule(firstMatch: string, stream: string): EventRuleModel {
  return {
    firstMatch,
    decoder: 'regex',
    decoderRegex: firstMatch,
    keyFormat: 'EVENT',
    mappings: [{ key: 'EVENT', annotate: [['Stream', stream], ['Action', 'EVENT']], translate: [], extra: [] }],
    fallback: { annotate: [['Stream', 'OTHER']], extra: [] },
    extra: [],
  }
}

/**
 * Highlight-to-extract on the model (the W3 text surgery, typed): extend the
 * rule's decoder-regex with the fragment (joined by `.*?`) and map the group
 * to `field` in every mapping's translate block. Appending means it reads
 * text to the right of what the rule already captures.
 */
export function addCaptureToRule(rule: EventRuleModel, fragment: string, group: string, field: string): void {
  const cur = s(rule.decoderRegex)
  rule.decoderRegex = cur ? `${cur}.*?${fragment}` : fragment
  if (!rule.mappings.length) rule.mappings.push({ key: 'EVENT', annotate: [], translate: [], extra: [] })
  for (const m of rule.mappings) {
    m.translate ??= []
    if (!m.translate.some(([k]) => k === group)) m.translate.push([group, field])
  }
}

/** Apply a decoder guess to a rule (and the model's kv-regex for the key-value decoder). */
export function applyDecoder(model: ExtractorModel, rule: EventRuleModel, guess: DecoderGuess): void {
  rule.decoder = guess.decoder
  if (guess.decoder === 'json') {
    delete rule.decoderRegex
    delete rule.decoderHeader
  } else if (guess.decoder === 'custom') {
    rule.decoderRegex = guess.decoderRegex ?? DEFAULT_KV_DECODER_REGEX
    if (guess.header) rule.decoderHeader = guess.header
    else delete rule.decoderHeader
    if (model.kvRegex === undefined) model.kvRegex = guess.kvRegex ?? DEFAULT_KV_REGEX
  } else {
    delete rule.decoderHeader
    if (rule.decoderRegex === undefined) rule.decoderRegex = s(rule.firstMatch)
  }
}

/** Keep `provides-streams` derived: mapping streams ∪ non-OTHER fallback streams (corpus rule, gate E6). */
export function deriveStreams(model: ExtractorModel): void {
  model.providesStreams = writtenStreams(model)
}

/* ------------------------------------------------------------ entries */

export function entryGet(entries: Entries | undefined, key: YamlKey): YamlScalar | undefined {
  return entries?.find(([k]) => k === key)?.[1]
}

export function entrySet(entries: Entries, key: YamlKey, value: YamlScalar): Entries {
  const i = entries.findIndex(([k]) => k === key)
  if (i >= 0) entries[i] = [key, value]
  else entries.push([key, value])
  return entries
}

export function entryDelete(entries: Entries, key: YamlKey): Entries {
  const i = entries.findIndex(([k]) => k === key)
  if (i >= 0) entries.splice(i, 1)
  return entries
}

export function mappingFor(rule: EventRuleModel, key: YamlKey): KeyMappingModel | undefined {
  return rule.mappings.find((m) => m.key === key)
}

/* ----------------------------------------------------------- simulator */

/**
 * Offline stand-in for `parsers/play` (demo mode only). Per sample: the first
 * rule whose first-match hits wins; its decoder yields the groups; the
 * mapping whose key equals the formatted event key (then one matching it as
 * a regex, then the empty-key mapping, then the first) annotates and
 * translates; globals translate always; `subs` rewrite values per field; no
 * rule at all → the first fallback's annotations with `Stream: OTHER` unless
 * it names a stream. The real engine does more (casts, multi-match); this is
 * a live preview for the demo YAML and is labelled as such.
 */
export function simulateModel(model: ExtractorModel, samples: string[], extractorId: number | string = 0): Array<Record<string, unknown>> {
  const base = (line: string): Record<string, unknown> => ({
    SourceName: s(model.sourceName), SourceType: s(model.sourceType), ExtractorID: Number(String(extractorId).split('_')[0]) || 0,
    PStatus: 'PAD', EvtLen: line.length, LogEvent: line,
  })
  const compiled = model.rules.map((r) => (s(r.firstMatch) ? toJsRegex(s(r.firstMatch)) : null))
  const declared = model.providesStreams.map((x) => (typeof x === 'string' ? x : '')).filter(Boolean)
  return samples.filter((l) => l.trim()).map((line) => {
    const row = base(line)
    const ri = compiled.findIndex((re) => re?.test(line))
    const rule = ri >= 0 ? model.rules[ri]! : undefined
    if (!rule) {
      const fb = model.rules.find((r) => r.fallback)?.fallback
      for (const [k, v] of fb?.annotate ?? []) row[String(k)] = v
      if (!row.Stream) row.Stream = 'OTHER'
      return row
    }
    const groups = extractGroups(model, rule, line)
    const key = formatKey(rule.keyFormat, groups)
    const mapping = mappingFor(rule, key)
      ?? rule.mappings.find((m) => typeof m.key === 'string' && m.key !== '' && (toJsRegex(`^${m.key}$`)?.test(key) ?? false))
      ?? mappingFor(rule, '')
      ?? rule.mappings[0]
    const put = (field: YamlKey, raw: string) => { if (raw !== undefined && raw !== '') row[String(field)] = raw }
    if (mapping) {
      for (const [k, v] of mapping.annotate ?? []) row[String(k)] = v
      for (const [g, field] of mapping.translate ?? []) { const v = groups[String(g)]; if (v !== undefined) put(s(field), v) }
    }
    if (rule.globals) {
      for (const [k, v] of rule.globals.annotate ?? []) if (row[String(k)] === undefined) row[String(k)] = v
      for (const [g, field] of rule.globals.translate ?? []) { const v = groups[String(g)]; if (v !== undefined) put(s(field), v) }
    }
    if (rule.subs) {
      for (const [field, table] of rule.subs) {
        const cur = row[String(field)]
        if (cur === undefined) continue
        const hit = table.find(([from]) => String(from) === String(cur))
        if (hit) row[String(field)] = hit[1]
      }
    }
    if (!row.Stream) row.Stream = declared[0] ?? 'OTHER'
    return row
  })
}

/** The YAML-text face of the simulator, what `usePlay` calls in demo mode. */
export function simulateDemo(yaml: string, samples: string[], extractorId: number | string = 0): Array<Record<string, unknown>> {
  let model: ExtractorModel
  try { model = fromYaml(yaml) } catch {
    return samples.filter((l) => l.trim()).map((line) => ({ ExtractorID: Number(String(extractorId).split('_')[0]) || 0, PStatus: 'PAD', EvtLen: line.length, LogEvent: line, Stream: 'OTHER' }))
  }
  return simulateModel(model, samples, extractorId)
}
